/**
 * Signed cookie session provider.
 *
 * Stores session data in signed cookies. No server-side storage required.
 */

import { createHmac } from 'node:crypto';

import type { Session, ISessionProvider } from '../../interfaces';

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Options for CookieSessionProvider.
 */
export interface CookieSessionProviderOptions {
  /** Secret for signing cookies (required) */
  secret: string;
  /** Session TTL in milliseconds (default: 7 days) */
  ttl?: number;
  /** Cookie name (default: 'mastra_session') */
  cookieName?: string;
  /** Cookie path (default: '/') */
  cookiePath?: string;
  /** Cookie domain */
  cookieDomain?: string;
  /** Use secure cookies (default: true in production) */
  secure?: boolean;
}

/**
 * Session data stored in cookie.
 */
interface CookieSessionData {
  id: string;
  userId: string;
  expiresAt: number; // Timestamp
  createdAt: number; // Timestamp
  metadata?: Record<string, unknown>;
}

/**
 * Signed cookie session provider.
 *
 * Stores session data in signed cookies. The session is validated
 * by verifying the signature on each request.
 *
 * @example
 * ```typescript
 * const sessionProvider = new CookieSessionProvider({
 *   secret: process.env.SESSION_SECRET!,
 *   ttl: 7 * 24 * 60 * 60 * 1000, // 7 days
 * });
 * ```
 */
export class CookieSessionProvider implements ISessionProvider {
  private secret: string;
  private ttl: number;
  private cookieName: string;
  private cookiePath: string;
  private cookieDomain?: string;
  private secure: boolean;

  constructor(options: CookieSessionProviderOptions) {
    if (!options.secret || options.secret.length < 32) {
      throw new Error('CookieSessionProvider requires a secret of at least 32 characters');
    }

    this.secret = options.secret;
    this.ttl = options.ttl ?? 7 * 24 * 60 * 60 * 1000; // 7 days
    this.cookieName = options.cookieName ?? 'mastra_session';
    this.cookiePath = options.cookiePath ?? '/';
    this.cookieDomain = options.cookieDomain;
    this.secure = options.secure ?? process.env['NODE_ENV'] === 'production';
  }

  async createSession(userId: string, metadata?: Record<string, unknown>): Promise<Session> {
    const now = Date.now();
    const session: Session = {
      id: crypto.randomUUID(),
      userId,
      expiresAt: new Date(now + this.ttl),
      createdAt: new Date(now),
      metadata,
    };

    return session;
  }

  async validateSession(_sessionId: string): Promise<Session | null> {
    // For cookie sessions, validation happens in getSessionFromCookie
    // This method is here for interface compliance
    return null;
  }

  async destroySession(_sessionId: string): Promise<void> {
    // Cookie sessions are destroyed by clearing the cookie
    // This is a no-op on the server side
  }

  async refreshSession(_sessionId: string): Promise<Session | null> {
    // For cookie sessions, we need the full session to refresh
    // This would be called with the session from getSessionFromCookie
    return null;
  }

  getSessionIdFromRequest(request: Request): string | null {
    const session = this.getSessionFromCookie(request);
    return session?.id ?? null;
  }

  /**
   * Get full session from cookie.
   */
  getSessionFromCookie(request: Request): Session | null {
    const cookieHeader = request.headers.get('cookie');
    if (!cookieHeader) return null;

    const escapedName = escapeRegExp(this.cookieName);
    const match = cookieHeader.match(new RegExp(`${escapedName}=([^;]+)`));
    if (!match?.[1]) return null;

    try {
      const decoded = this.decodeAndVerify(match[1]);
      if (!decoded) return null;

      // Check expiration
      if (decoded.expiresAt < Date.now()) {
        return null;
      }

      return {
        id: decoded.id,
        userId: decoded.userId,
        expiresAt: new Date(decoded.expiresAt),
        createdAt: new Date(decoded.createdAt),
        metadata: decoded.metadata,
      };
    } catch {
      return null;
    }
  }

  getSessionHeaders(session: Session): Record<string, string> {
    const data: CookieSessionData = {
      id: session.id,
      userId: session.userId,
      expiresAt: session.expiresAt.getTime(),
      createdAt: session.createdAt.getTime(),
      metadata: session.metadata,
    };

    const encoded = this.signAndEncode(data);
    const maxAge = Math.floor((session.expiresAt.getTime() - Date.now()) / 1000);

    let cookie = `${this.cookieName}=${encoded}; HttpOnly; SameSite=Lax; Path=${this.cookiePath}; Max-Age=${maxAge}`;

    if (this.cookieDomain) {
      cookie += `; Domain=${this.cookieDomain}`;
    }

    if (this.secure) {
      cookie += '; Secure';
    }

    return { 'Set-Cookie': cookie };
  }

  getClearSessionHeaders(): Record<string, string> {
    let cookie = `${this.cookieName}=; HttpOnly; SameSite=Lax; Path=${this.cookiePath}; Max-Age=0`;

    if (this.cookieDomain) {
      cookie += `; Domain=${this.cookieDomain}`;
    }

    return { 'Set-Cookie': cookie };
  }

  /**
   * Sign and encode session data.
   */
  private signAndEncode(data: CookieSessionData): string {
    const json = JSON.stringify(data);
    const signature = this.sign(json);
    const payload = `${this.base64Encode(json)}.${signature}`;
    return encodeURIComponent(payload);
  }

  /**
   * Decode and verify session cookie.
   */
  private decodeAndVerify(cookie: string): CookieSessionData | null {
    try {
      const decoded = decodeURIComponent(cookie);
      const [data, signature] = decoded.split('.');

      if (!data || !signature) return null;

      const json = this.base64Decode(data);
      const expectedSignature = this.sign(json);

      // Constant-time comparison
      if (!this.secureCompare(signature, expectedSignature)) {
        return null;
      }

      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  /**
   * Create HMAC-SHA256 signature.
   */
  private sign(data: string): string {
    return createHmac('sha256', this.secret).update(data).digest('base64url');
  }

  /**
   * Base64 encode (consistent across Node.js and browser runtimes).
   */
  private base64Encode(str: string): string {
    // Use TextEncoder for consistent UTF-8 handling across runtimes
    const bytes = new TextEncoder().encode(str);
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(bytes).toString('base64');
    }
    // Browser fallback: btoa only handles Latin1, so convert bytes to a binary string
    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary);
  }

  /**
   * Base64 decode (consistent across Node.js and browser runtimes).
   */
  private base64Decode(str: string): string {
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(str, 'base64').toString('utf-8');
    }
    // Browser fallback: atob returns a binary string, convert back to UTF-8
    const binary = atob(str);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
  }

  /**
   * Constant-time string comparison.
   */
  private secureCompare(a: string, b: string): boolean {
    if (a.length !== b.length) return false;

    let result = 0;
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }

    return result === 0;
  }
}
